import React, { useState } from 'react';
import { MoreVertical, AlertTriangle, X } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import chatService from '../../services/chatService';
import toast from 'react-hot-toast';

const ChatHeader = ({ conversaAtual, online, onFechar }) => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [loading, setLoading] = useState(false);

  if (!conversaAtual) {
    return (
      <div className="p-4 bg-primary-500 text-white border-b border-primary-600 flex items-center justify-center">
        <h3 className="text-lg font-semibold">Selecione uma conversa</h3>
      </div>
    );
  }

  const handleReport = async () => {
    if (!motivo) {
      toast.error('Selecione um motivo');
      return;
    }

    setLoading(true);
    try {
      await chatService.criarReport({
        usuarioReportadoId: conversaAtual.outroUsuario.id,
        conversaId: conversaAtual.conversaId,
        motivo,
        descricao,
      });

      toast.success('Conversa reportada com sucesso');
      setShowReportModal(false);
      setMotivo('');
      setDescricao('');
    } catch (error) {
      toast.error('Erro ao reportar conversa');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="p-4 bg-primary-500 text-white border-b border-primary-600">
        <div className="flex items-center justify-between">
          {/* Info do usuário */}
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="relative">
              {conversaAtual.outroUsuario?.fotoPerfil ? (
                <img
                  src={conversaAtual.outroUsuario.fotoPerfil}
                  alt={conversaAtual.outroUsuario.nome}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/default-profile.webp';
                  }}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-semibold">
                  {conversaAtual.outroUsuario.nome.charAt(0).toUpperCase()}
                </div>
              )}
              {/* Indicador Online */}
              <div
                className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-primary-500 rounded-full ${
                  online ? 'bg-green-400' : 'bg-gray-400'
                }`}
              ></div>
            </div>

            {/* Nome e status */}
            <div>
              <h3 className="font-semibold text-lg">
                {conversaAtual.outroUsuario.nome}
              </h3>
              <p className="text-xs text-white/80">
                {online ? 'Online' : 'Offline'}
              </p>
            </div>
          </div>

          {/* Ações */}
          <div className="flex items-center gap-2">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="min-w-[180px] bg-white dark:bg-dark-card rounded-lg shadow-lg border border-gray-200 dark:border-dark-border p-1 z-50"
                  sideOffset={5}
                >
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-bg rounded cursor-pointer outline-none"
                    onSelect={() => setShowReportModal(true)}
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Reportar conversa
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>

            {onFechar && (
              <button
                onClick={onFechar}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors md:hidden"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Report */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
                Reportar Conversa
              </h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
                  Motivo *
                </label>
                <select
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Selecione um motivo</option>
                  <option value="spam">Spam</option>
                  <option value="abuso">Abuso</option>
                  <option value="conteudo_inapropriado">Conteúdo Inapropriado</option>
                  <option value="assedio">Assédio</option>
                  <option value="outro">Outro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
                  Descrição (opcional)
                </label>
                <textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={4}
                  placeholder="Descreva o problema..."
                  className="w-full px-3 py-2 border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  disabled={loading}
                  className="flex-1 px-4 py-2 border border-gray-200 dark:border-dark-border text-gray-700 dark:text-dark-text rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReport}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Reportando...' : 'Reportar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatHeader;
